import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { injectIntl, FormattedMessage, FormattedDate } from 'react-intl';

// Initial component setup for airbnb/react-dates library
import { DayPickerSingleDateController } from 'react-dates';
import { SingleDatePickerPhrases } from 'react-dates/lib/defaultPhrases';
import getPhrasePropTypes from 'react-dates/lib/utils/getPhrasePropTypes';

import { childrenShape } from '@bibliocommons/bc-prop-types';
import { Angle } from '@bibliocommons/deprecated-base-icons';
import { TextButton, SecondaryTextButton, SecondaryGhostButton } from '@bibliocommons/base-buttons';
import { CalendarEmpty } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

// Initial css setup for airbnb/react-dates library
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import './react_dates_overrides.scss';

import FullScreenOverlay from 'app/components/deprecated/shared/FullScreenOverlay';
import NativeDatePicker from './NativeDatePicker';
import './AccessibleDatePicker.scss';

function formatDate(date, format = 'YYYY-MM-DD') {
  if (!date) return '';
  const dateObj = moment.isMoment(date) ? date : moment(date);
  return dateObj.format(format);
}

function isMobile() {
  return typeof window !== 'undefined' && /Android|iPhone|iPad/i.test(window.navigator.userAgent);
}

export function AccessibleDatePicker(props) {
  const {
    initialDate,
    open,
    id,
    handleClose,
    handleOpen,
    dataKey,
    intl,
    children,
    fullScreen,
    className,
    minDate,
    maxDate,
    highlightSelectedDateRange: highlightRange,
    buttonTag: ButtonTag = SecondaryGhostButton,
    buttonTagClassName,
    disabled,
    showLoadingText,
    isLoading,
    onDateChange,
    ariaDescribedBy
  } = props;

  const [date, setDate] = useState(initialDate ? moment(initialDate) : null);
  const datePickerRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  // Sync state with initialDate prop changes
  useEffect(() => {
    if (formatDate(initialDate) !== formatDate(date)) {
      setDate(initialDate ? moment(initialDate) : null);
    }
  }, [initialDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle clicks outside the date picker to close it
  useEffect(() => {
    function handleDocumentClick(e) {
      const wrapper = datePickerRef.current;
      if (wrapper && wrapper !== e.target && !wrapper.contains(e.target)) {
        handleClose();
      }
    }

    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [handleClose]);

  // Unmount-only: the close timer scheduled by handleDateChange must not outlive the component.
  //
  // The clearTimeout is the primary cancel. isMountedRef is a second line of defence for the case
  // where it silently fails to take effect, which is not hypothetical: a REAL timer scheduled
  // before sinon's useFakeTimers() is installed survives clearTimeout entirely, because the
  // clearTimeout being called is then the fake clock's, which knows nothing about the real handle.
  // Demonstrated directly (CORE-78937). The flag does not depend on cancellation working -- this
  // cleanup still runs -- so the callback can decline to touch the parent either way.
  //
  // Guarding here rather than in each parent is deliberate. An earlier attempt guarded DateField and
  // EditHoldDatesBatchActions instead (see archive/CORE-78937-parent-guards) and was abandoned: this
  // component cannot outlive its parent, so a parent-side guard models a scenario that cannot occur.
  // The failure being defended against is the opposite one -- the timer surviving its own
  // cancellation -- which only the owner of the timer can see. There are five consumers
  // (DateField, EditHoldDatesBatchActions, HoldsExpiryDate, HoldsPauseDateLink, HoldsPauseDateSelect)
  // and they all share this shape, so one guard here covers them all.
  // Armed inside the effect, not only at the useRef initialiser. React's Fast Refresh re-runs
  // effects on an in-place edit WITHOUT unmounting, so a cleanup-only flag latches false and never
  // recovers -- the picker silently stops auto-closing in all five consumers until a full reload.
  // Dev-only, but the cleanup it replaced was idempotent and this would not have been. Matches the
  // two in-repo precedents, useWindowWidth.js:32 and Modal.jsx:119-122. Raised by Heitor in review.
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const handleDateChange = useCallback(
    newDate => {
      onDateChange(formatDate(newDate));
      setDate(newDate ? moment(newDate) : null);
      // Held in a ref so unmount can cancel it. Uncancelled, this fires handleClose() up to 100ms
      // after the component is gone, and the parent sets state on an unmounted tree -- the same
      // defect class as CORE-78819 / CORE-78822. In the console.error-throwing test harness the
      // resulting React warning lands on whichever unrelated test happens to be running, which is
      // how it surfaced: as an intermittent failure in the DateField suite (CORE-78851).
      //
      // Clear before rescheduling: the ref holds one handle, so a second selection inside the 100ms
      // window would otherwise overwrite the first and orphan it -- unreachable, uncancellable, and
      // still due to fire. Reachable in the UI by picking two days quickly, or Today then Reset,
      // both of which route through here.
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) {
          return;
        }
        handleClose();
      }, 100);
    },
    [onDateChange, handleClose]
  );

  const isOutsideRange = useCallback(
    day => {
      const isBeforeMinDate = minDate && day.isBefore(moment(minDate), 'day');
      const isAfterMaxDate = maxDate && day.isAfter(moment(maxDate), 'day');

      if (minDate && maxDate) return isBeforeMinDate || isAfterMaxDate;
      if (minDate) return isBeforeMinDate;
      if (maxDate) return isAfterMaxDate;
      return false;
    },
    [minDate, maxDate]
  );

  const isDayHighlighted = useCallback(
    day => highlightRange && moment(day).isAfter(minDate) && moment(day).isBefore(date),
    [highlightRange, minDate, date]
  );

  const phraseOverrides = {
    ...SingleDatePickerPhrases,
    jumpToPrevMonth: intl.formatMessage({ id: 'calendar_prev_month' }),
    jumpToNextMonth: intl.formatMessage({ id: 'calendar_next_month' }),
    dateIsUnavailable: ({ date: d }) => intl.formatMessage({ id: 'calendar_date_is_unavailable' }, { date: d }),
    dateIsSelected: ({ date: d }) => intl.formatMessage({ id: 'calendar_date_is_selected' }, { date: d })
  };

  function renderTodayButton() {
    return (
      <SecondaryTextButton
        className="item-column"
        dataKey="today-button"
        handleClick={() => handleDateChange(moment().format())}
      >
        <FormattedMessage
          id="calendar_today"
          values={{
            date: (
              <ScreenReaderMessage>
                <FormattedDate value={moment().format()} year="numeric" month="short" day="2-digit" />
              </ScreenReaderMessage>
            )
          }}
        />
      </SecondaryTextButton>
    );
  }

  function renderCloseButton() {
    return (
      <TextButton className="item-column" dataKey="close-button" handleClick={handleClose}>
        <FormattedMessage id="button_close" />
      </TextButton>
    );
  }

  function renderResetButton() {
    if (!date) return null;
    return (
      <SecondaryTextButton
        className="item-column"
        data-test-id="reset-button"
        dataKey="reset-button"
        handleClick={() => handleDateChange(null)}
      >
        <FormattedMessage id="reset" />
      </SecondaryTextButton>
    );
  }

  function renderExtraButtons() {
    return (
      <div className="date-picker-extra-buttons item-row">
        {renderTodayButton()}
        {renderResetButton()}
        {renderCloseButton()}
      </div>
    );
  }

  function renderChildren() {
    return (
      <ButtonTag
        dataKey={dataKey}
        handleClick={handleOpen}
        className={buttonTagClassName}
        disabled={disabled}
        showLoadingText={showLoadingText}
        isLoading={isLoading}
        aria-hidden={isMobile()}
        tabIndex={isMobile() ? '-1' : undefined}
        aria-describedby={ariaDescribedBy}
      >
        <CalendarEmpty />
        <span>{children}</span>
      </ButtonTag>
    );
  }

  function renderDatePickerPopUp(pickerProps) {
    return (
      <div className="cp-accessible-date-picker-pop-up" ref={datePickerRef}>
        <div className="cp-accessible-date-picker-content">{renderChildren()}</div>
        {open && (
          <DayPickerSingleDateController
            {...pickerProps}
            isOutsideRange={isOutsideRange}
            date={date}
            withPortal={false}
            phrases={phraseOverrides}
            onDateChange={handleDateChange}
            renderCalendarInfo={renderExtraButtons}
            weekDayFormat="ddd"
            isDayHighlighted={isDayHighlighted}
          />
        )}
      </div>
    );
  }

  function renderFullScreenOverlayDatePicker(pickerProps) {
    return (
      <div>
        <FullScreenOverlay
          className="accessible-date-picker-full-screen-overlay"
          open={open}
          handleClose={handleClose}
          dataKey="accessible-date-picker-full-screen-overlay"
        >
          <DayPickerSingleDateController
            {...pickerProps}
            isOutsideRange={isOutsideRange}
            date={date}
            phrases={phraseOverrides}
            onDateChange={handleDateChange}
            renderCalendarInfo={renderExtraButtons}
            weekDayFormat="ddd"
          />
        </FullScreenOverlay>
        <div className="cp-accessible-date-picker-content">{renderChildren()}</div>
      </div>
    );
  }

  function renderNativeDatePicker() {
    return (
      <NativeDatePicker
        className="mobile-datepicker"
        id={`${id}-native-picker`}
        value={formatDate(date)}
        onChange={handleDateChange}
        minDate={formatDate(minDate)}
        maxDate={formatDate(maxDate)}
      >
        {renderChildren()}
      </NativeDatePicker>
    );
  }

  function renderContent(pickerProps) {
    if (isMobile()) return renderNativeDatePicker();
    return fullScreen ? renderFullScreenOverlayDatePicker(pickerProps) : renderDatePickerPopUp(pickerProps);
  }

  const pickerProps = _.omit(props, [
    'initialDate',
    'open',
    'id',
    'handleClose',
    'handleOpen',
    'dataKey',
    'intl',
    'children',
    'displayFormat',
    'inputIconPosition',
    'fullScreen',
    'className',
    'minDate',
    'maxDate',
    'highlightSelectedDateRange',
    // CORE-78851. Everything NOT in this list is spread onto DayPickerSingleDateController, which
    // rejects unknown props via airbnb-prop-types -- a console.error in production, but a hard
    // failure under the console.error-throwing test harness, which is why no consumer rendering the
    // editable branch had a test covering it.
    //
    // The rule is "omit every prop the calendar does not accept", NOT "omit every prop this wrapper
    // destructures". Those are different, and the second one is the trap: small, focus and
    // customInputIcon were declared in propTypes -- advertised as supported -- while being neither
    // destructured nor omitted, so passing any of them broke the calendar. They were dead surface
    // (no consumer passed them, nothing here read them) and have been deleted from propTypes rather
    // than added below, since omitting them would have preserved the pretence of support.
    //
    // The remaining props below are consumed by this wrapper and applied to the trigger button in
    // renderChildren; they have no meaning to the calendar.
    //
    // onDateChange is the exception: it IS a valid calendar prop, and the explicit
    // onDateChange={handleDateChange} later in the JSX overrides the spread, so leaking it was
    // harmless. Omitted anyway so this list means one thing rather than two.
    'ariaDescribedBy',
    'buttonTag',
    'buttonTagClassName',
    'disabled',
    'isLoading',
    'onDateChange',
    'showLoadingText'
  ]);

  return <div className={`cp-accessible-date-picker ${className}`}>{renderContent(pickerProps)}</div>;
}

const defaultProps = {
  numberOfMonths: 1,
  displayFormat: () => moment.localeData().longDateFormat('L'),
  inputIconPosition: 'after',
  navPrev: <Angle.Left />,
  navNext: <Angle.Right />,
  hideKeyboardShortcutsPanel: true,
  withPortal: true,
  initialDate: null,
  focused: true,
  fullScreen: false,
  className: '',
  minDate: moment().format('YYYY-MM-DD')
};

const propTypes = {
  open: PropTypes.bool,
  id: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  children: childrenShape,
  fullScreen: PropTypes.bool,
  buttonTag: PropTypes.elementType,
  buttonTagClassName: PropTypes.string,
  className: PropTypes.string,
  dataKey: PropTypes.string,
  handleOpen: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showLoadingText: PropTypes.bool,
  isLoading: PropTypes.bool,
  initialDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onDateChange: PropTypes.func.isRequired,
  focused: PropTypes.bool,
  onFocusChange: PropTypes.func,
  navPrev: PropTypes.node,
  navNext: PropTypes.node,
  phrases: PropTypes.shape(getPhrasePropTypes(SingleDatePickerPhrases)),
  noBorder: PropTypes.bool,
  hideKeyboardShortcutsPanel: PropTypes.bool,
  displayFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  withPortal: PropTypes.bool,
  inputIconPosition: PropTypes.string,
  numberOfMonths: PropTypes.number,
  renderCalendarInfo: PropTypes.func,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  highlightSelectedDateRange: PropTypes.bool,
  ariaDescribedBy: PropTypes.string
};

AccessibleDatePicker.propTypes = propTypes;
AccessibleDatePicker.defaultProps = defaultProps;

export default injectIntl(AccessibleDatePicker);
